import { Breadcrumb, Button, Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import Loading from '../../Components/common/Loading';
import NoDataFound from '../../Components/common/NoDataFound';
import Time from '../../Components/Times/timesOne.js';
import { getActivities } from '../../services/apiService';
import './activity.css';

const { Option } = Select;

// Service/group filter options (maps to the backend log_name column)
const LOG_NAME_OPTIONS = [
    { value: '', label: 'All Activities' },
    { value: 'auth', label: 'Login / Logout' },
    { value: 'ird_menu', label: 'IRD Menu Management' },
    { value: 'restaurant_menu', label: 'Restaurant Menu Management' },
    { value: 'spa_menu', label: 'Spa Menu Management' },
    { value: 'laundry_menu', label: 'Laundry Menu Management' },
    { value: 'scheduler', label: 'Menu Scheduler' },
    { value: 'ird_order', label: 'IRD Orders' },
    { value: 'ird_restaurant_order', label: 'Restaurant Orders' },
    { value: 'spa_order', label: 'Spa Orders' },
    { value: 'laundry_order', label: 'Laundry Orders' },
    { value: 'connect_order', label: 'Connect Orders' },
    { value: 'booking_order', label: 'Booking Orders' },
];

// snake_case event -> "Title Case" label
const prettify = (str = '') =>
    str
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

// 'App\MenuItem' -> 'Menu Item'
const prettifySubject = (subjectType = '') => {
    const base = subjectType.split('\\').pop() || '';
    return base.replace(/([a-z])([A-Z])/g, '$1 $2');
};

// properties is stored double-wrapped ([ {...} ]) by the logger; normalize it
const normalizeProperties = (properties) => {
    if (!properties) return {};
    if (Array.isArray(properties)) return properties[0] || {};
    return properties;
};

// The affected entity: subject_id is set for orders; for other subjects the
// real id lives in properties.subject_id (the orders foreign key forces
// subject_id to be null for non-order rows).
const resolveEntity = (item) => {
    const props = normalizeProperties(item.properties);
    const id = item.subject_id ?? props.subject_id ?? null;
    const type = item.subject_type ? prettifySubject(item.subject_type) : null;
    if (!type && !id) return null;
    return `${type || 'Record'}${id ? ` #${id}` : ''}`;
};

// The acting user: prefer the eager-loaded causer, fall back to the username
// captured in properties (covers rows whose causer relation is missing).
const resolveUser = (item) => {
    const props = normalizeProperties(item.properties);
    const name =
        item.causer?.name ||
        props.causer_name ||
        item.causer?.username ||
        props.causer_username ||
        '—';
    const type = item.causer?.type || null;
    return { name, type };
};

// Cap very long values (e.g. a serialized object) so a single row can't
// blow the table width out.
const truncate = (str = '', max = 140) =>
    str.length > max ? str.slice(0, max) + '…' : str;

// Render the field-level diff / created payload for a row
const ActivityDetails = ({ item }) => {
    const props = normalizeProperties(item.properties);
    const changes = props.changes;
    const entity = resolveEntity(item);

    const body = (() => {

    if (changes && Object.keys(changes).length > 0) {
        return (
            <ul className='activity-changes'>
                {Object.entries(changes).map(([field, val]) => (
                    <li key={field}>
                        <b>{prettify(field)}:</b>{' '}
                        <span className='old-val'>
                            {val?.old === null || val?.old === ''
                                ? '—'
                                : String(val?.old)}
                        </span>{' '}
                        &rarr;{' '}
                        <span className='new-val'>
                            {val?.new === null || val?.new === ''
                                ? '—'
                                : String(val?.new)}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }

    if (props.attributes && props.attributes.name) {
        return <span>Created "{String(props.attributes.name)}"</span>;
    }
    if (props.reason) {
        return <span>Reason: {String(props.reason)}</span>;
    }
    if (props.filters && Object.keys(props.filters).length > 0) {
        return (
            <span className='activity-filters'>
                {Object.entries(props.filters)
                    .map(([k, v]) => {
                        const val =
                            typeof v === 'object'
                                ? JSON.stringify(v)
                                : String(v);
                        return `${prettify(k)}: ${truncate(val)}`;
                    })
                    .join(', ')}
            </span>
        );
    }
    if (props.device_type) {
        return <span>Device: {String(props.device_type)}</span>;
    }
    return <span>—</span>;
    })();

    return (
        <>
            {entity && <div className='activity-subject'>{entity}</div>}
            {body}
        </>
    );
};

const ActivityLog = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [lastPage, setLastPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [logName, setLogName] = useState('');

    const searchRef = useRef();
    const startDateRef = useRef();
    const endDateRef = useRef();

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {
                page: pageNumber,
                per_page: perPage,
            };
            if (logName) params.log_name = logName;
            if (searchRef.current?.value) params.search = searchRef.current.value;
            if (startDateRef.current?.value)
                params.date_start = startDateRef.current.value;
            if (endDateRef.current?.value)
                params.date_end = endDateRef.current.value;

            const response = await getActivities(params);
            if (response.status === 200) {
                const paginator = response.data?.data || {};
                setActivities(paginator.data || []);
                setLastPage(paginator.last_page || 0);
                setTotal(paginator.total || 0);
            }
        } catch (error) {
            console.log(error);
            setActivities([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, perPage, logName]);

    const applyFilters = () => {
        if (pageNumber !== 1) {
            setPageNumber(1); // triggers fetch via effect
        } else {
            fetchData();
        }
    };

    const clearFilters = () => {
        if (searchRef.current) searchRef.current.value = '';
        if (startDateRef.current) startDateRef.current.value = '';
        if (endDateRef.current) endDateRef.current.value = '';
        setLogName('');
        setPageNumber(1);
    };

    return (
        <>
            <div className='action-header'>
                <div className='action-left'>
                    <div className='page-title'>ACTIVITY LOG</div>
                    <Breadcrumb
                        items={[
                            { title: <a href='/'>Home</a> },
                            { title: 'Activity Log' },
                        ]}
                    />
                </div>
                <div className='action-right'>
                    <Button
                        type='primary'
                        onClick={() => setShowFilters((prev) => !prev)}
                    >
                        Filter
                    </Button>
                </div>
            </div>

            <section className='order-history-wrapper'>
                <div className='aside-right'>
                    <div className='home-order-screen'>
                        <table className='table table-striped table-hover table-history activity-table'>
                            <thead>
                                <tr>
                                    <th className='text-center'>DATE &amp; TIME</th>
                                    <th className='text-center'>USER</th>
                                    <th className='text-center'>ACTIVITY</th>
                                    <th className='text-center'>DESCRIPTION</th>
                                    <th className='text-center'>DETAILS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr style={{ backgroundColor: 'transparent' }}>
                                        <td colSpan='5'>
                                            <Loading />
                                        </td>
                                    </tr>
                                ) : activities.length === 0 ? (
                                    <tr style={{ backgroundColor: 'transparent' }}>
                                        <td colSpan='5'>
                                            <NoDataFound />
                                        </td>
                                    </tr>
                                ) : (
                                    activities.map((item) => {
                                        const user = resolveUser(item);
                                        return (
                                        <tr key={item.id}>
                                            <td className='text-center'>
                                                <Time
                                                    newDate={item.created_at}
                                                    timesString={item.created_at}
                                                />
                                            </td>
                                            <td className='text-center'>
                                                {user.name}
                                                {user.type ? (
                                                    <div className='activity-user-type'>
                                                        {prettify(user.type)}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className='text-center'>
                                                {prettify(item.event)}
                                            </td>
                                            <td>{item.description}</td>
                                            <td>
                                                <ActivityDetails item={item} />
                                            </td>
                                        </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className='pagination-container'>
                        <div className='form-group'>
                            <label>Total Activities: </label>{' '}
                            <label>
                                <b>{total}</b>
                            </label>
                        </div>
                        <div className='form-group'>
                            <label>Row per Page: </label>
                            <select
                                name='count'
                                id='count'
                                value={perPage}
                                onChange={(e) => {
                                    setPerPage(Number(e.target.value));
                                    setPageNumber(1);
                                }}
                            >
                                <option value='20'>20</option>
                                <option value='50'>50</option>
                                <option value='100'>100</option>
                                <option value='200'>200</option>
                            </select>
                        </div>
                        {lastPage > 1 && (
                            <ReactPaginate
                                previousLabel={'Previous'}
                                nextLabel={'Next'}
                                breakLabel={'...'}
                                breakClassName={'break-me'}
                                pageCount={lastPage}
                                forcePage={pageNumber - 1}
                                marginPagesDisplayed={2}
                                pageRangeDisplayed={2}
                                onPageChange={(e) =>
                                    setPageNumber(e.selected + 1)
                                }
                                containerClassName={'pagination'}
                                subContainerClassName={'pages pagination'}
                                activeClassName={'active'}
                            />
                        )}
                    </div>
                </div>

                {showFilters && (
                    <div className='aside-left'>
                        <div className='aside_main_wrapper'>
                            <div className='filter-type-option'>
                                <label htmlFor='log_name'>Activity Type</label>
                                <Select
                                    id='log_name'
                                    value={logName}
                                    onChange={(value) => {
                                        setLogName(value || '');
                                        setPageNumber(1);
                                    }}
                                >
                                    {LOG_NAME_OPTIONS.map((opt) => (
                                        <Option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className='filter-type-option'>
                                <label htmlFor='start_date'>Start Date:</label>
                                <input
                                    type='date'
                                    id='start_date'
                                    name='start_date'
                                    ref={startDateRef}
                                />
                            </div>
                            <div className='filter-type-option'>
                                <label htmlFor='end_date'>End Date:</label>
                                <input
                                    type='date'
                                    id='end_date'
                                    name='end_date'
                                    ref={endDateRef}
                                />
                            </div>
                            <div className='filter-type-option'>
                                <label className='filtertype-label'>
                                    Search
                                </label>
                                <input
                                    type='text'
                                    ref={searchRef}
                                    className='form-control search__input'
                                    placeholder='Search description / action'
                                />
                            </div>
                            <div className='btns-wrapper'>
                                <Button type='primary' onClick={applyFilters}>
                                    Apply filters
                                </Button>
                                <Button type='default' onClick={clearFilters}>
                                    Clear filters
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </>
    );
};

export default ActivityLog;
